/* Instagram modal for Community cards — modal + new-tab behavior in one file */
(() => {
  const SEL_ANY_SECTION = '[data-carousel="community"]';
  const SEL_CARD = '.atd-comm-card';

  const MODAL_ID = 'atd-insta-modal-root';
  const ANIM_MS = 180;
  /* --- Scrollbar compensation (mirrors YT modal) --- */
  const state = { locked: false, prev: { overflow: '', padRight: '' }, sbw: 0 };

  function getScrollbarWidth() {
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    return sbw > 0 ? sbw : 0;
  }

  function lockScrollWithCompensation() {
    if (state.locked) return;
    const scrollEl = document.scrollingElement || document.documentElement;
    const html = document.documentElement;

    state.sbw = getScrollbarWidth();
    state.prev.overflow = scrollEl.style.overflow;
    state.prev.padRight = scrollEl.style.paddingRight;

    const currentPad = parseFloat(getComputedStyle(scrollEl).paddingRight || '0') || 0;
    if (state.sbw > 0) {
      scrollEl.style.paddingRight = (currentPad + state.sbw) + 'px';
      html.style.setProperty('--atd-sbw', state.sbw + 'px');
    }

    scrollEl.style.overflow = 'hidden';
    html.classList.add('atd-insta-modal--open');
    state.locked = true;
  }

  function unlockScrollWithCompensation() {
    if (!state.locked) return;
    const scrollEl = document.scrollingElement || document.documentElement;
    const html = document.documentElement;

    scrollEl.style.overflow = state.prev.overflow || '';
    scrollEl.style.paddingRight = state.prev.padRight || '';
    html.style.removeProperty('--atd-sbw');
    html.classList.remove('atd-insta-modal--open');

    state.locked = false;
  }

  /* --- Instagram embed loader (one-time) --- */
  function ensureInstaScript() {
    if (window.instgrm && window.instgrm.Embeds) return Promise.resolve();
    return new Promise((res) => {
      const s = document.createElement('script');
      s.src = 'https://www.instagram.com/embed.js';
      s.async = true;
      s.onload = () => res();
      document.head.appendChild(s);
    });
  }

  /* --- Modal DOM (loader lives inside the overlay, centered) --- */
  function ensureModal() {
    let root = document.getElementById(MODAL_ID);
    if (root) return root;

    root = document.createElement('div');
    root.id = MODAL_ID;
    root.innerHTML = `
      <div class="atd-insta-modal__overlay" data-m-overlay>
        <style>
          #${MODAL_ID} .atd-insta-modal__overlay {
            display:none;
            opacity:0;
            transition: opacity .18s ease;
            position:absolute; inset:0;
            background: rgba(0,0,0,.6);
          }
          #${MODAL_ID} .atd-insta-modal__overlay.is-open { display:grid; place-items:center; }
          #${MODAL_ID} .atd-insta-modal__overlay.is-visible { opacity:1; }

          #${MODAL_ID} .atd-insta-loader { display:grid; place-items:center; }
          #${MODAL_ID} .atd-spinner {
            width:36px; height:36px; border-radius:50%;
            border:3px solid rgba(255,255,255,.35);
            border-top-color:#fff;
            animation: atd-spin .8s linear infinite;
          }
          @keyframes atd-spin { to { transform: rotate(360deg); } }

          #${MODAL_ID} .atd-insta-modal__content { display:none; }
          #${MODAL_ID} .atd-insta-modal__content.is-ready { display:block; }
        </style>
        <div class="atd-insta-loader" data-m-loader>
          <div class="atd-spinner" aria-hidden="true"></div>
        </div>
      </div>

      <div class="atd-insta-modal__shell" data-m-shell>
        <div class="atd-insta-modal__dialog" role="dialog" aria-modal="true" aria-label="Instagram post" data-m-dialog>
          <button class="atd-insta-modal__close" type="button" aria-label="Close" data-m-close>✕</button>
          <div class="atd-insta-modal__content" data-m-content></div>
        </div>
      </div>
    `;
    document.body.appendChild(root);

    const close = () => closeModal(root);
    root.querySelector('[data-m-overlay]').addEventListener('click', close);
    root.querySelector('[data-m-close]').addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (root.dataset.open === 'true' && e.key === 'Escape') close();
    });

    return root;
  }

  /* Wait until IG converts blockquote to iframe & it loads */
  function waitForEmbedReady(mount, done) {
    const once = (() => { let hit = false; return () => { if (hit) return; hit = true; done(); }; })();

    const hook = () => {
      const ifr = mount.querySelector('iframe[src*="instagram.com"]');
      if (ifr) {
        ifr.addEventListener('load', once, { once: true });
        setTimeout(once, 2500);
        return true;
      }
      return false;
    };

    if (hook()) return;

    const mo = new MutationObserver(() => { if (hook()) mo.disconnect(); });
    mo.observe(mount, { childList: true, subtree: true });
    setTimeout(() => { mo.disconnect(); once(); }, 4000);
  }

  async function openModalWithEmbed({ html, url }) {
    const root = ensureModal();
    const overlay = root.querySelector('[data-m-overlay]');
    const shell = root.querySelector('[data-m-shell]');
    const dialog = root.querySelector('[data-m-dialog]');
    const mount = root.querySelector('[data-m-content]');
    const loader = root.querySelector('[data-m-loader]');

    root.dataset.open = 'true';
    lockScrollWithCompensation();

    // Reset UI
    loader.style.display = 'grid';
    mount.classList.remove('is-ready');
    mount.innerHTML = '';

    // Build payload
    let payload = html && html.trim();
    if (!payload && url) {
      const safe = url.replace(/"/g, '&quot;');
      payload = `<blockquote class="instagram-media" data-instgrm-permalink="${safe}" data-instgrm-version="14" style="max-width:680px;width:100%;margin:0 auto;"></blockquote>`;
    }
    if (!payload) return;

    // Show modal, then process
    shell.classList.add('is-open');
    requestAnimationFrame(() => {
      overlay.classList.add('is-open', 'is-visible');
      dialog.classList.add('is-visible');
    });

    mount.innerHTML = payload;

    await ensureInstaScript();
    if (window.instgrm && window.instgrm.Embeds) window.instgrm.Embeds.process();

    // Wait for IG iframe, then hide loader & reveal content
    waitForEmbedReady(mount, () => {
      loader.style.display = 'none';
      mount.classList.add('is-ready');
    });
  }

  function closeModal(root) {
    const overlay = root.querySelector('[data-m-overlay]');
    const shell = root.querySelector('[data-m-shell]');
    const dialog = root.querySelector('[data-m-dialog]');
    const mount = root.querySelector('[data-m-content]');
    const loader = root.querySelector('[data-m-loader]');

    root.dataset.open = 'false';
    overlay.classList.remove('is-visible');
    dialog.classList.remove('is-visible');

    setTimeout(() => {
      overlay.classList.remove('is-open');
      shell.classList.remove('is-open');
      unlockScrollWithCompensation();
      if (loader) loader.style.display = 'grid';
      if (mount) { mount.classList.remove('is-ready'); mount.innerHTML = ''; }
    }, ANIM_MS);
  }

  /* --- Unified click handler (modal OR new tab per section setting) --- */
  function onClick(e) {
    const card = e.target.closest(SEL_CARD);
    if (!card) return;

    const section = e.target.closest(SEL_ANY_SECTION);
    if (!section) return;

    const wantsModal = section.dataset.useInstaModal === 'true';

    // Always stop the carousel’s default navigation when we handle
    if (wantsModal) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const html = card.getAttribute('data-insta-embed-html') || '';
      const url = card.getAttribute('data-insta-url') || '';
      openModalWithEmbed({ html, url });
    } else if (section.dataset.useInstaModal === 'false') {
      const url = card.getAttribute('data-insta-url') || '';
      if (!url) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      window.open(url, '_blank', 'noopener');
    }
  }

  function init() {
    // Capture phase so we beat other listeners (e.g., carousel)
    document.addEventListener('click', onClick, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
